import { useCallback } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { determineUserFlow } from '@dispatch/shared/services';
import { UserFlowResponse } from '@dispatch/shared/types';
import { saveAuthorityToStorage } from '../lib/AuthorityStorage';

export interface UseUserFlowReturn {
  determineFlow: (email: string) => Promise<string>;
  isLoading: boolean;
  error: string | null;
  flowType: 'federated' | 'local' | null;
}

// Query key for user flow - includes email for cache separation
const getUserFlowQueryKey = (email: string) =>
  ['auth', 'userFlow', email] as const;

export function useUserFlow(): UseUserFlowReturn {
  const queryClient = useQueryClient();

  const mutation = useMutation<UserFlowResponse, Error, string>(
    (email: string) => determineUserFlow(email),
    {
      retry: (failureCount, error) => {
        // Only retry on specific server errors that might be transient
        // Don't retry on client errors (4xx) except for specific cases
        const status = (error as Error & { response?: { status?: number } })
          ?.response?.status;

        // Retry on:
        // - Network errors (no status)
        // - 502 Bad Gateway
        // - 503 Service Unavailable
        // - 504 Gateway Timeout
        // - 429 Too Many Requests (rate limiting)
        if (
          !status ||
          status === 502 ||
          status === 503 ||
          status === 504 ||
          status === 429
        ) {
          return failureCount < 2;
        }

        return false;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      onSuccess: (data, email) => {
        // Store the authority for future token refresh operations
        if (data.userFlowUrl) {
          saveAuthorityToStorage(data.userFlowUrl, data.flowType);
          // Cache the result for this email
          queryClient.setQueryData(getUserFlowQueryKey(email), data);
        }
      },
    }
  );

  const determineFlow = useCallback(
    async (email: string): Promise<string> => {
      // Check if we already have cached data for this email
      const cachedData = queryClient.getQueryData<UserFlowResponse>(
        getUserFlowQueryKey(email)
      );

      if (cachedData?.userFlowUrl) {
        // Update the current flow type from cache
        return cachedData.userFlowUrl;
      }

      // No cache, fetch new data
      const result = await mutation.mutateAsync(email);

      if (!result.userFlowUrl) {
        throw new Error('API did not return a user flow URL');
      }

      return result.userFlowUrl;
    },
    [mutation, queryClient]
  );

  const errorMessage = mutation.error
    ? (mutation.error as Error & { response?: { data?: { message?: string } } })
        ?.response?.data?.message ||
      mutation.error.message ||
      'Failed to determine user flow'
    : null;

  return {
    determineFlow,
    isLoading: mutation.isLoading,
    error: errorMessage,
    flowType: mutation.data?.flowType || null,
  };
}
